import { useState } from 'react';
import toast from 'react-hot-toast';
import { authApi } from '@/features/auth/auth.api';
import { useAuthContext } from '@/features/auth/AuthProvider';
import { extractError } from '@/lib/axios';

export default function MfaSection(): JSX.Element {
  const { user } = useAuthContext();
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [enabled, setEnabled] = useState(user?.mfaEnabled ?? false);

  const beginSetup = async () => {
    try {
      const { qrDataUrl } = await authApi.setupMfa();
      setQr(qrDataUrl);
    } catch (err) {
      toast.error(extractError(err).message);
    }
  };

  const confirmEnable = async () => {
    try {
      const { backupCodes: codes } = await authApi.enableMfa(code);
      setBackupCodes(codes);
      setEnabled(true);
      setQr(null);
      toast.success('MFA enabled');
    } catch (err) {
      toast.error(extractError(err).message);
    }
  };

  const disable = async () => {
    const token = window.prompt('Enter a current MFA code to disable:');
    if (!token) return;
    try {
      await authApi.disableMfa(token);
      setEnabled(false);
      toast.success('MFA disabled');
    } catch (err) {
      toast.error(extractError(err).message);
    }
  };

  return (
    <div className="card">
      <h3 className="font-semibold">Two-factor authentication</h3>
      <p className="mt-1 text-sm text-slate-500">Add an extra layer of security with an authenticator app.</p>

      {enabled ? (
        <button className="btn-ghost mt-4 border border-red-300 text-red-600" onClick={disable}>
          Disable MFA
        </button>
      ) : qr ? (
        <div className="mt-4 space-y-3">
          <img src={qr} alt="MFA QR code" className="h-44 w-44 rounded border" />
          <p className="text-sm text-slate-500">Scan with Google Authenticator / Authy, then enter the 6-digit code.</p>
          <div className="flex gap-2">
            <input className="input max-w-[140px]" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" />
            <button className="btn-primary" onClick={confirmEnable}>Verify & enable</button>
          </div>
        </div>
      ) : (
        <button className="btn-primary mt-4" onClick={beginSetup}>Enable MFA</button>
      )}

      {backupCodes && (
        <div className="mt-4 rounded-lg bg-amber-50 p-3 text-sm dark:bg-amber-900/20">
          <p className="font-medium">Save these backup codes:</p>
          <div className="mt-2 grid grid-cols-2 gap-1 font-mono">
            {backupCodes.map((c) => <span key={c}>{c}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}
